"use client";

import { Trans } from "@lingui/react/macro";
import { PlusIcon } from "lucide-react";
import { usePathname } from "next/navigation";
import { SidebarGroupAction } from "@/components/ui/sidebar";
import { DASHBOARD_POLICIES } from "@/lib/authorization/policies/dashboard";
import { useCheckPolicy } from "@/lib/authorization/use-check-policy";
import CreateDashboardDialog from "../../dialog/create-dashboard-dialog";
import CollapsibleProjectGroup from "./collapsible-project-group";
import SortableDashboards from "./sortable-dashboards";
import type { CollapsibleState } from "./types";
import type { WorkspaceSidebarModel } from "./workspace-sidebar-data-model";

type Props = {
  workspaceId: string;
  dashboardData: WorkspaceSidebarModel["dashboards"];
  initialGroupState: CollapsibleState;
};

export function DashboardSidebarGroup({
  workspaceId,
  dashboardData,
  initialGroupState,
}: Props) {
  const currentPathname = usePathname();
  const checkPolicy = useCheckPolicy();
  const canEditDashboards = checkPolicy(
    DASHBOARD_POLICIES["dashboard:edit"],
  ).ok;

  return (
    <CollapsibleProjectGroup
      groupType="dashboards"
      title={<Trans>DASHBOARDS</Trans>}
      initialState={initialGroupState}
      action={
        canEditDashboards ? (
          <CreateDashboardDialog
            trigger={
              <SidebarGroupAction>
                <PlusIcon className="w-4 h-4" />
                <span className="sr-only">
                  <Trans>Create new dashboard</Trans>
                </span>
              </SidebarGroupAction>
            }
            workspaceId={workspaceId}
          />
        ) : undefined
      }
    >
      <SortableDashboards
        workspaceId={workspaceId}
        dashboards={dashboardData.items}
        dashboardOrder={dashboardData.order}
        currentPathname={currentPathname}
        isReorderEnabled={canEditDashboards}
      />
    </CollapsibleProjectGroup>
  );
}
